import { useEffect } from "react";
import type { AgeGroup } from "../design-tokens";
import { AgeGroupContext } from "../primitives/context";
import {
  AGE_GROUPS,
  colors,
  spacing,
  shellChrome,
  layoutViewport,
  touchTargets,
  typography,
  borderRadius,
  shadows,
  transitions,
  zIndex,
} from "../design-tokens";

// ─── Props ────────────────────────────────────────────────────

export interface ThemeProviderProps {
  children: React.ReactNode;
  ageGroup?: AgeGroup;
}

// ─── CSS Variable Mapping ─────────────────────────────────────

// Un `ageGroup` fuori dalle fasce note (es. `ageGroup="teacher"`, tipizzato
// male o passato da JS non tipizzato) rendeva `typography[ageGroup]` undefined:
// il primo `.heading` lanciava e l'app restava schermo bianco, con un errore
// illeggibile nel bundle minificato (incidente prod qr-reader, 2026-09-11).
// Il fallback è esplicito e loggato — mai silenzioso.
export function resolveAgeGroup(ageGroup: AgeGroup): AgeGroup {
  if ((AGE_GROUPS as readonly string[]).includes(ageGroup)) return ageGroup;

  console.error(
    `[ThemeProvider] ageGroup non valido: ${String(ageGroup)} — ` +
      `previsti: ${AGE_GROUPS.join(", ")}. Uso il fallback "${DEFAULT_AGE_GROUP}".`
  );
  return DEFAULT_AGE_GROUP;
}

function buildCssVariables(ageGroup: AgeGroup): Record<string, string> {
  const resolvedAgeGroup = resolveAgeGroup(ageGroup);
  const ageTypography = typography[resolvedAgeGroup];
  const ageTouch = touchTargets[resolvedAgeGroup];

  return {
    // Colors — Primary
    "--tl4k-primary-50": colors.primary[50],
    "--tl4k-primary-100": colors.primary[100],
    "--tl4k-primary-200": colors.primary[200],
    "--tl4k-primary-300": colors.primary[300],
    "--tl4k-primary-400": colors.primary[400],
    "--tl4k-primary-500": colors.primary[500],
    "--tl4k-primary-600": colors.primary[600],
    "--tl4k-primary-700": colors.primary[700],
    "--tl4k-primary-800": colors.primary[800],
    "--tl4k-primary-900": colors.primary[900],

    // Colors — Secondary
    "--tl4k-secondary-50": colors.secondary[50],
    "--tl4k-secondary-100": colors.secondary[100],
    "--tl4k-secondary-200": colors.secondary[200],
    "--tl4k-secondary-300": colors.secondary[300],
    "--tl4k-secondary-400": colors.secondary[400],
    "--tl4k-secondary-500": colors.secondary[500],
    "--tl4k-secondary-600": colors.secondary[600],
    "--tl4k-secondary-700": colors.secondary[700],
    "--tl4k-secondary-800": colors.secondary[800],
    "--tl4k-secondary-900": colors.secondary[900],

    // Colors — Tertiary
    "--tl4k-tertiary-50": colors.tertiary[50],
    "--tl4k-tertiary-100": colors.tertiary[100],
    "--tl4k-tertiary-200": colors.tertiary[200],
    "--tl4k-tertiary-300": colors.tertiary[300],
    "--tl4k-tertiary-400": colors.tertiary[400],
    "--tl4k-tertiary-500": colors.tertiary[500],
    "--tl4k-tertiary-600": colors.tertiary[600],
    "--tl4k-tertiary-700": colors.tertiary[700],
    "--tl4k-tertiary-800": colors.tertiary[800],
    "--tl4k-tertiary-900": colors.tertiary[900],

    // Colors — Status
    "--tl4k-success": colors.success,
    "--tl4k-warning": colors.warning,
    "--tl4k-error": colors.error,
    "--tl4k-info": colors.info,

    // Colors — Status surface
    "--tl4k-success-surface": colors.successSurface,
    "--tl4k-warning-surface": colors.warningSurface,
    "--tl4k-error-surface": colors.errorSurface,
    "--tl4k-info-surface": colors.infoSurface,

    // Colors — Neutral
    "--tl4k-neutral-50": colors.neutral[50],
    "--tl4k-neutral-100": colors.neutral[100],
    "--tl4k-neutral-200": colors.neutral[200],
    "--tl4k-neutral-300": colors.neutral[300],
    "--tl4k-neutral-400": colors.neutral[400],
    "--tl4k-neutral-500": colors.neutral[500],
    "--tl4k-neutral-600": colors.neutral[600],
    "--tl4k-neutral-700": colors.neutral[700],
    "--tl4k-neutral-800": colors.neutral[800],
    "--tl4k-neutral-900": colors.neutral[900],

    // Colors — Surface
    "--tl4k-surface-default": colors.surface.default,
    "--tl4k-surface-warm": colors.surface.warm,
    "--tl4k-surface-neutral": colors.surface.neutral,
    "--tl4k-surface-cool": colors.surface.cool,
    // `containerLow`/`containerHigh` non sono nel tipo dichiarato di `surface`:
    // se un tema non li definisce la custom property deve comunque essere una
    // stringa, altrimenti finirebbe "undefined" nel CSS.
    "--tl4k-surface-container-low":
      (colors.surface as Record<string, string | undefined>).containerLow ?? colors.surface.default,
    "--tl4k-surface-container-high":
      (colors.surface as Record<string, string | undefined>).containerHigh ?? colors.surface.default,

    // Colors — Focus & Outline
    "--tl4k-focus": colors.focus,
    "--tl4k-focus-ring": colors.focusRing,
    "--tl4k-outline": (colors as Record<string, unknown>).outline as string,
    "--tl4k-outline-variant": (colors as Record<string, unknown>).outlineVariant as string,

    // Spacing
    "--tl4k-spacing-xs": spacing.xs,
    "--tl4k-spacing-sm": spacing.sm,
    "--tl4k-spacing-md": spacing.md,
    "--tl4k-spacing-lg": spacing.lg,
    "--tl4k-spacing-xl": spacing.xl,
    "--tl4k-spacing-2xl": spacing["2xl"],
    "--tl4k-spacing-3xl": spacing["3xl"],
    "--tl4k-spacing-4xl": spacing["4xl"],

    // Typography
    "--tl4k-font-family": typography.fontFamily,
    "--tl4k-font-family-headline": typography.fontFamilyHeadline,
    "--tl4k-font-family-body": typography.fontFamilyBody,
    "--tl4k-font-family-mono": typography.fontFamilyMono,
    "--tl4k-font-size-heading": ageTypography.heading,
    "--tl4k-font-size-subheading": ageTypography.subheading,
    "--tl4k-font-size-body": ageTypography.body,
    "--tl4k-font-size-small": ageTypography.small,
    "--tl4k-font-size-button": ageTypography.button,
    "--tl4k-line-height": ageTypography.lineHeight,

    // Touch Targets
    "--tl4k-touch-target-size": ageTouch.minSize,
    "--tl4k-touch-icon-style": ageTouch.iconStyle,
    "--tl4k-touch-label-position": ageTouch.labelPosition,
    "--tl4k-touch-feedback": ageTouch.feedback,
    "--tl4k-shell-chrome-height": shellChrome.heightPx,
    "--tl4k-shell-utility-button-height": shellChrome.utilityButtonHeightPx,
    "--tl4k-shell-utility-button-min-width": `${shellChrome.utilityButtonMinWidth}px`,
    "--tl4k-shell-utility-icon-size": `${shellChrome.utilityIconSize}px`,
    "--tl4k-shell-anchor-size": `${shellChrome.anchorSize}px`,

    // Layout Viewport
    "--tl4k-layout-embedded-py": layoutViewport.embeddedPaddingY,
    "--tl4k-layout-standalone-py": layoutViewport.standalonePaddingY,
    "--tl4k-layout-compact-gap": layoutViewport.compactGap,
    "--tl4k-layout-normal-gap": layoutViewport.normalGap,
    "--tl4k-layout-info-strip-h": layoutViewport.infoStripHeightPx,
    "--tl4k-layout-toolbar-h": layoutViewport.toolbarHeightPx,

    // Border Radius
    "--tl4k-border-radius-sm": borderRadius.sm,
    "--tl4k-border-radius-md": borderRadius.md,
    "--tl4k-border-radius-lg": borderRadius.lg,
    "--tl4k-border-radius-xl": borderRadius.xl,
    "--tl4k-border-radius-2xl": borderRadius["2xl"],
    "--tl4k-border-radius-DEFAULT": borderRadius.DEFAULT,
    "--tl4k-border-radius-pill": borderRadius.pill,
    "--tl4k-border-radius-full": borderRadius.full,

    // Shadows
    "--tl4k-shadow-sm": shadows.sm,
    "--tl4k-shadow-md": shadows.md,
    "--tl4k-shadow-lg": shadows.lg,
    "--tl4k-shadow-xl": shadows.xl,

    // Transitions
    "--tl4k-transition-fast": transitions.fast,
    "--tl4k-transition-base": transitions.base,
    "--tl4k-transition-slow": transitions.slow,

    // Z-Index
    "--tl4k-z-base": String(zIndex.base),
    "--tl4k-z-dropdown": String(zIndex.dropdown),
    "--tl4k-z-sticky": String(zIndex.sticky),
    "--tl4k-z-overlay": String(zIndex.overlay),
    "--tl4k-z-modal": String(zIndex.modal),
    "--tl4k-z-popover": String(zIndex.popover),
    "--tl4k-z-toast": String(zIndex.toast),
  };
}

// ─── ThemeProvider ────────────────────────────────────────────

const DEFAULT_AGE_GROUP: AgeGroup = "4-6";

export function ThemeProvider({ children, ageGroup = DEFAULT_AGE_GROUP }: ThemeProviderProps) {
  useEffect(() => {
    const root = document.documentElement;
    const variables = buildCssVariables(ageGroup);

    for (const [key, value] of Object.entries(variables)) {
      root.style.setProperty(key, value);
    }

    return () => {
      for (const key of Object.keys(variables)) {
        root.style.removeProperty(key);
      }
    };
  }, [ageGroup]);

  // Oltre alle CSS variables, l'ageGroup viaggia in un React context: serve
  // alle primitive per risolvere `touch={true}` nel minimo della fascia.
  // Additivo e retrocompatibile — chi usava ThemeProvider non cambia nulla.
  return <AgeGroupContext.Provider value={ageGroup}>{children}</AgeGroupContext.Provider>;
}
